/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components). Prod had
   its own hand-rolled copy on the Plan rail; that one now calls this. */
import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { BiPlus } from 'react-icons/bi';

import s from './AddSlot.module.scss';

/**
 * The dashed tile that ends a row of cards: "put another one here".
 *
 * Distinct from {@link AddButton}, which is the same intent in a *list* — a
 * full-width row on the items' own left edge. This is the tile shape, for a
 * rail or a grid of cards, and it stands where the next card will appear.
 *
 * **Why it exists.** The dashed "empty slot" was a convention rather than a
 * component: ~40 hand-rolled instances across Crew, Vessel, Discover, Profile,
 * Files and Plan, drawing their edge from six different border tokens. Two of
 * those disagreed in dark only — `--color-border-medium` resolves to an opaque
 * slate where `--track-border` is a 16% white — so the same card rendered a
 * heavier edge on one screen than on another, in the theme the app ships. One
 * component, one token, one hover.
 *
 * Not every dashed edge in the app is this: a drop zone, a disabled preview
 * placeholder and a "pending" state also use dashes and are not "add another".
 * Reach for this one only when pressing it creates something.
 *
 * **Two sizes, and they are a scale rather than a compromise.** The slot's label
 * should match the titles of the cards it stands beside, because it stands in
 * their row: `md` is 18/22 for the 260px place tiles in Library, `sm` is 14px
 * for the 196px trip cards on Plan. Anything else is a third size — add a step
 * here rather than overriding the label in a screen's stylesheet.
 *
 * @example
 * <AddSlot label="Plan a trip" hint="Type, dates, who is coming" size="sm" onClick={…} />
 *
 * @alias add card, new tile, empty slot, plus tile, create card
 */
export const AddSlot = ({
  label,
  hint,
  icon,
  size = 'md',
  className,
  ref,
  ...props
}: {
  /** The action, as a verb phrase — "Create new", "Plan a trip". */
  label: string;
  /** One line on what pressing it asks for. Optional: some slots need no gloss. */
  hint?: ReactNode;
  /** Defaults to a plus. Override only when a plus would be the wrong verb. */
  icon?: ReactNode;
  /** `md` (default) beside 18px card titles; `sm` beside 14–16px ones. */
  size?: 'sm' | 'md';
  /* Declared rather than inherited: React 19 passes `ref` as an ordinary prop,
     but `ButtonHTMLAttributes` does not type it. Callers anchor menus to the
     slot, so it has to reach the button. */
  ref?: Ref<HTMLButtonElement>;
} & ButtonHTMLAttributes<HTMLButtonElement>) => (
  /* Width is the caller's: a rail sets its own track (260px in Library, 196px
     on Plan) and the slot has no opinion on how wide the row it joins is. It
     owns everything else. */
  <button
    type="button"
    ref={ref}
    className={clsx(s.slot, size === 'sm' && s.sm, className)}
    {...props}
  >
    <span className={s.icon} aria-hidden="true">
      {icon ?? <BiPlus size={size === 'sm' ? 18 : 22} />}
    </span>
    <span className={s.text}>
      <span className={s.label}>{label}</span>
      {hint && <span className={s.hint}>{hint}</span>}
    </span>
  </button>
);
